/**
 * Currency utility functions
 */

import { getCurrencyDef } from '../config/currencies';

/**
 * Currency symbol mapping
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  BRL: 'R$',
  MXN: '$',
  CHF: 'CHF',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  HRK: 'kn',
  RUB: '₽',
  TRY: '₺',
  THB: '฿',
  IDR: 'Rp',
  MYR: 'RM',
  PHP: '₱',
  SGD: 'S$',
  VND: '₫',
  ZAR: 'R',
  HKD: 'HK$',
  NZD: 'NZ$',
  KRW: '₩',
  AED: 'د.إ',
  SAR: 'ر.س',
  EGP: 'E£',
  NGN: '₦',
  KES: 'KSh',
};

/**
 * Get currency symbol for a given currency code
 * @param currency - ISO currency code (e.g., 'USD', 'EUR')
 * @returns Currency symbol (e.g., '$', '€')
 */
export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency.toUpperCase()] || currency;
}

/**
 * Format amount with currency symbol
 * @param amount - Amount to format
 * @param currency - ISO currency code
 * @returns Formatted string (e.g., '$10.00', '€10.00')
 */
export function formatCurrency(amount: number, currency: string): string {
  const symbol = getCurrencySymbol(currency);
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatter.format(amount)}`;
}

/**
 * Format price without trailing zeros
 * @param price - Price to format
 * @returns Formatted string (e.g., '10', '10.5', '10.99')
 */
export function formatPrice(price: number): string {
  // Remove trailing zeros: 10.00 -> 10, 10.50 -> 10.5, 10.99 -> 10.99
  return price.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

/**
 * Stripe zero-decimal currencies (no minor unit) that are NOT in the currency registry.
 * Registry currencies (e.g. VUV) carry their own `zeroDecimal` flag; this set just keeps
 * decimalsFor() correct for any extra codes (JPY/KRW) that only have a display symbol.
 */
const ZERO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW']);

/**
 * Number of decimal places to display for a currency.
 * Sourced from the currency registry (single source of truth); falls back to the static
 * zero-decimal set for currencies the registry doesn't define. 0 for zero-decimal, else 2.
 */
export function decimalsFor(currency: string): number {
  const cur = currency.toUpperCase();
  const def = getCurrencyDef(cur);
  if (def) return def.zeroDecimal ? 0 : 2;
  return ZERO_DECIMAL_CURRENCIES.has(cur) ? 0 : 2;
}

/**
 * Format an amount for display in the active currency.
 *
 * - USD / EUR / GBP / CAD / AUD / NZD (glyph symbols): `$11.99`, `€39.99`, `A$59.99` —
 *   byte-identical to the previous `${symbol}${formatPrice(amount)}` output.
 * - Alphabetic symbols (e.g. CHF): a separating space reads better → `CHF 39.99`.
 * - VUV (zero-decimal): rounded integer with a suffixed code, e.g. `180 VUV`.
 *
 * @param amount - Amount in major units (dollars / vatu)
 * @param currency - ISO currency code; defaults to USD
 */
export function formatMoney(amount: number, currency: string = 'USD'): string {
  const cur = currency.toUpperCase();
  if (decimalsFor(cur) === 0) {
    return `${Math.round(amount)} ${cur}`;
  }
  const symbol = getCurrencySymbol(cur);
  // Alphabetic symbols (CHF, kr, …) read better with a space; glyphs ($ € £ C$) don't.
  const sep = /[A-Za-z]$/.test(symbol) ? ' ' : '';
  return `${symbol}${sep}${formatPrice(amount)}`;
}

/**
 * Round a discounted price with smart .99 preservation.
 * If original price ends in .99 and discounted price would round to X.00 or X.98, adjust to X.99
 *
 * Examples:
 * - 39.99 * 0.5 = 19.995 → rounds to 20.00 → adjusted to 19.99
 * - 19.99 * 0.5 = 9.995 → rounds to 10.00 → adjusted to 9.99
 *
 * @param originalAmount - The original price before discount
 * @param discountedAmount - The calculated discounted price
 * @returns The properly rounded price
 */
export function smartRoundPrice(originalAmount: number, discountedAmount: number): number {
  const rounded = Math.round(discountedAmount * 100) / 100;
  const originalCents = Math.round(originalAmount * 100) % 100;
  const roundedCents = Math.round(rounded * 100) % 100;

  // If original ends in .99 and rounded ends in .00 or .98, adjust to .99
  if (originalCents === 99 && (roundedCents === 0 || roundedCents === 98)) {
    const wholePart = Math.floor(rounded);
    // If rounded to X.00, use (X-1).99; if rounded to X.98, use X.99
    return roundedCents === 0 ? wholePart - 1 + 0.99 : wholePart + 0.99;
  }

  return rounded;
}

/**
 * Get human-readable period text for billing periods
 * @param period - Period code (e.g., 'w', 'm', '3m', 'y')
 * @returns Human-readable period text (e.g., 'per week', 'per month')
 */
export function getPeriodText(period: string): string {
  switch (period) {
    case 'w': return 'per week';
    case 'm': return 'per month';
    case '3m': return 'every 3 months';
    case 'y': return 'per year';
    case '4w': return 'every 4 weeks';
    case '12w': return 'every 12 weeks';
    default: return 'per month';
  }
}

/**
 * Update disclaimer price elements within a container
 * @param container - Parent element to search within
 * @param option - Processed option with prices and configs
 */
export function updateDisclaimerPrices(container: Element, option: {
  processed_prices: { pay_now?: number; recurring?: number; pay_later?: number };
  price_configs?: { recurring?: { period?: string } };
  currency?: string;
}): void {
  const introPriceElement = container.querySelector('.disclaimer-price-intro');
  const standardPriceElement = container.querySelector('.disclaimer-price-standard');
  const periodElement = container.querySelector('.disclaimer-period');

  if (!introPriceElement || !standardPriceElement) return;

  // Get prices from the option
  const introPrice = option.processed_prices.pay_now || 0;
  const standardPrice = option.processed_prices.pay_later || option.processed_prices.recurring || 0;

  // Format prices in the option's currency (USD output is unchanged).
  const currency = option.currency || 'USD';

  introPriceElement.textContent = formatMoney(introPrice, currency);
  standardPriceElement.textContent = formatMoney(standardPrice, currency);

  // Update billing period text
  if (periodElement) {
    const period = option.price_configs?.recurring?.period || 'm';
    periodElement.textContent = getPeriodText(period);
  }
}
